import React, { useEffect, useState } from 'react';
import Input from '@/presentation/reusables/Input';
import ReusableButton from '@/presentation/reusables/Button';
import ReusableInfo from '@/presentation/reusables/Info';
import { SInputToggle, SInputToggleInput, SInputToggleLabel } from './styled';
import type { TActionState, TInput } from '../../types';
import type { TProfile } from '../../../../helpers/format';
import GoogleAnalytics from '@/presentation/hooks/useGoogleAnalytics';

interface IProps {
  actionState: TActionState;
  inputType: TInput;
  profile: TProfile;
  controllerActionState: (state: Partial<TActionState>) => void;
}

const InputToggleOld = ({
  actionState,
  inputType,
  profile,
  controllerActionState
}: IProps) => {
  const [hasPermission, setHasPermission] = useState(false);
  const [textPermission, setTextPermission] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [value, setValue] = useState('');

  const types: any = {
    EMAIL: {
      label: 'Correo',
      placeholder: 'Ingresa tu correo',
      type: 'email',
      value: profile?.email?.toLowerCase()
    },
    PHONE: {
      label: 'Celular',
      placeholder: 'Ingresa tu celular',
      type: 'phone',
      value: profile?.phone?.replace(/^51/, '')
    },
    DEFAULT: {
      label: '',
      placeholder: '',
      type: 'custom',
      value: ''
    }
  };

  useEffect(() => {
    const { input } = actionState;
    setHasPermission(false);
    setIsActive(input === inputType);
    setIsDisabled(input !== inputType && input !== 'DEFAULT');
    setTextPermission('');

    const hasAll = !profile?.emailPermission && !profile?.phonePermission;
    const hasEmail = inputType === 'EMAIL' && !profile?.emailPermission;
    const hasPhone = inputType === 'PHONE' && !profile?.phonePermission;

    if (hasAll) {
      setIsDisabled(true);
      if (inputType === 'EMAIL') {
        setHasPermission(true);
        setTextPermission('celular y correo.');
      }
    } else if (hasEmail) {
      setHasPermission(true);
      setIsDisabled(true);
      setTextPermission('correo.');
    } else if (hasPhone) {
      setHasPermission(true);
      setIsDisabled(true);
      setTextPermission('celular.');
    }
  }, [actionState, profile]);

  useEffect(() => {
    if (isActive) {
      controllerActionState({
        newValue: types[inputType].value,
        oldValue: types[inputType].value
      });
      setValue(types[inputType].value);
    } else {
      setValue('');
      controllerActionState({
        newValue: '',
        oldValue: ''
      });
    }
  }, [isActive]);

  useEffect(() => {
    if (value !== actionState.newValue) {
      controllerActionState({ newValue: value });
    }
  }, [value]);

  const getFieldLabel = (label: string) => {
    switch (label) {
      case 'Celular':
        return 'Cell';
      case 'Correo':
        return 'Mail';
      default:
        return '';
    }
  };

  const handlerConfirm = () => {
    controllerActionState({
      action: 'CONFIRM',
      input: inputType,
      status: 'DEFAULT'
    });
  };

  const handlerToggle = () => {
    const label = getFieldLabel(types[inputType].label);
    if (!isActive) {
      GoogleAnalytics.writeLog(`portal-profile-clickEdit${label}`, 'profile');
    } else {
      GoogleAnalytics.writeLog(`portal-profile-clickCancel${label}`, 'profile');
    }
    controllerActionState({
      input: actionState.input === inputType ? 'DEFAULT' : inputType,
      status: 'DEFAULT'
    });
  };

  const onSetIsValid: any = (v: boolean) => {
    setIsValid(v && actionState.oldValue !== value);
  };

  return (
    <div>
      <SInputToggle isActive={isActive}>
        <SInputToggleLabel isActive={isActive} isDisabled={isDisabled}>
          <div>
            <p>{types[inputType]?.label}</p>
            <p
              style={{
                color:
                  types[inputType]?.value?.length > 0 ? '#161D1F' : '#4F6168'
              }}
            >
              {types[inputType]?.value?.length > 0
                ? types[inputType].value
                : types[inputType].placeholder}
            </p>
          </div>
          <button
            disabled={isDisabled}
            type="button"
            onClick={handlerToggle}
            data-testid="input-toggle-button"
          >
            <span>{isActive ? 'Cancelar' : 'Editar'}</span>
          </button>
        </SInputToggleLabel>
        {isActive && (
          <SInputToggleInput>
            <Input
              placeholder={types[inputType]?.placeholder}
              setIsValid={onSetIsValid}
              setValue={setValue}
              type={types[inputType]?.type || 'custom'}
              value={value}
            />
            <ReusableButton
              disabled={!isValid}
              onClick={handlerConfirm}
              text="Validar"
              type="button"
            />
          </SInputToggleInput>
        )}
      </SInputToggle>
      {hasPermission && (
        <ReusableInfo
          backgroundColor="#fabd00"
          iconColor="#161D1F"
          text={`Espera 24 horas después de la última actualización para hacer modificaciones en tu <span>${textPermission}</span>`}
          textColor="#161D1F"
          style={{ marginTop: '24px' }}
        />
      )}
    </div>
  );
};

export default InputToggleOld;
